/**
 * BOXEND – barre de recherche : fait remonter les marques phares.
 *
 * À chaque recherche, en plus de la recherche normale de Shopify, on demande les produits
 * Moncler / Burberry / Nike / Under Armour qui correspondent aux mots tapés, puis on range la liste « Produits » :
 *   1) produits qui contiennent tous les mots tapés, puis les autres ;
 *   2) dans chaque groupe : vestes Brésil épinglées > les 4 marques > le reste.
 * Si quoi que ce soit échoue, la recherche normale s'affiche telle quelle.
 *
 * La même liste est utilisée pour la page « Résultats de la recherche » dans snippets/bx-search-rank.liquid.
 */
import { sectionRenderer } from '@theme/section-renderer';

const BRANDS = ['Moncler', 'Burberry', 'Nike', 'Under Armour'];
// Vestes Brésil épinglées en tête : noire, blanche, rose (identifiant Shopify + adresse du produit)
const PINNED = [
  { id: '16584677818759', handle: 'veste-bresil-noir' },
  { id: '16584677753223', handle: 'veste-bresil-blanche' },
  { id: '16584677851527', handle: 'veste-bresil-rose' },
];
const PINNED_HANDLES = PINNED.map((product) => product.handle);
const PINNED_QUERY = PINNED.map((product) => `id:${product.id}`).join(' OR ');
const MAX_PRODUCTS = 8; // même nombre que la liste « Produits » du thème

const BRANDS_QUERY = `(${BRANDS.map((brand) => (brand.includes(' ') ? `vendor:"${brand}"` : `vendor:${brand}`)).join(' OR ')})`;
const BRANDS_NORMALIZED = BRANDS.map((brand) => normalize(brand));

/** @param {string} text */
function normalize(text) {
  return (text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

/** @param {string} text */
function searchWords(text) {
  return normalize(text)
    .split(/[^a-z0-9]+/)
    .filter((word) => word.length > 2)
    .map((word) => (word.length > 3 && word.endsWith('s') ? word.slice(0, -1) : word));
}

/** @param {Element} item */
function handleOf(item) {
  const link = item.querySelector('a[href*="/products/"]');
  const match = link?.getAttribute('href')?.match(/\/products\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]) : '';
}

/** @param {Element} item */
function vendorOf(item) {
  return item instanceof HTMLElement ? item.dataset.bxVendor || '' : '';
}

/** @param {Element} item */
function keyOf(item) {
  return normalize(`${handleOf(item).replace(/-/g, ' ')} ${vendorOf(item)} ${item.textContent || ''}`);
}

/**
 * @param {Element} item
 * @param {string[]} words
 */
function matchesAll(item, words) {
  const key = keyOf(item);
  return words.every((word) => key.includes(word));
}

/**
 * @param {Element} item
 * @param {string[]} words
 * @param {Set<string>} brandHandles
 */
function levelOf(item, words, brandHandles) {
  const handle = handleOf(item);
  const vendor = normalize(vendorOf(item));
  const isPinned = PINNED_HANDLES.includes(handle);
  const isBrand = brandHandles.has(handle) || (vendor !== '' && BRANDS_NORMALIZED.includes(vendor));
  const base = isPinned ? 0 : isBrand ? 1 : 2;
  return matchesAll(item, words) ? base : base + 3;
}

/** Les vestes épinglées restent dans l'ordre de la liste PINNED (noire, blanche, rose). @param {Element} item */
function pinnedRank(item) {
  const position = PINNED_HANDLES.indexOf(handleOf(item));
  return position === -1 ? PINNED_HANDLES.length : position;
}

const PRODUCTS_LIST = '#predictive-search-products .predictive-search-results__wrapper-products';

/**
 * @param {string} markup - Résultats de la recherche normale
 * @param {string | null} brandMarkup - Résultats limités aux 4 marques
 * @param {string | null} pinnedMarkup - Vestes Brésil épinglées
 * @param {string[]} words - Mots tapés
 */
function mergeResults(markup, brandMarkup, pinnedMarkup, words) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(markup, 'text/html');
  const inner = doc.querySelector('[data-search-results]');
  if (!inner) return markup;

  const brandDoc = brandMarkup ? parser.parseFromString(brandMarkup, 'text/html') : null;
  let list = inner.querySelector(PRODUCTS_LIST);

  const nativeItems = list ? Array.from(list.children).filter((el) => el.tagName === 'LI') : [];
  const brandItems = brandDoc ? Array.from(brandDoc.querySelectorAll(`${PRODUCTS_LIST} > li`)) : [];
  const brandHandles = new Set(brandItems.map(handleOf).filter(Boolean));
  const pinnedDoc = pinnedMarkup ? parser.parseFromString(pinnedMarkup, 'text/html') : null;
  const pinnedItems = pinnedDoc ? Array.from(pinnedDoc.querySelectorAll(`${PRODUCTS_LIST} > li`)) : [];

  /** @type {Element[]} */
  const pool = [];
  const seen = new Set();

  for (const item of nativeItems) {
    const handle = handleOf(item);
    if (!handle || seen.has(handle)) continue;
    seen.add(handle);
    pool.push(item);
  }

  for (const item of [...pinnedItems, ...brandItems]) {
    const handle = handleOf(item);
    if (!handle || seen.has(handle) || !matchesAll(item, words)) continue;
    seen.add(handle);
    pool.push(doc.importNode(item, true));
  }

  if (pool.length === 0) return markup;

  const ranked = pool
    .map((item, index) => ({ item, index, level: levelOf(item, words, brandHandles) }))
    .sort((a, b) => a.level - b.level || pinnedRank(a.item) - pinnedRank(b.item) || a.index - b.index)
    .slice(0, MAX_PRODUCTS)
    .map(({ item }) => item);

  if (!list) {
    // La recherche normale n'a renvoyé aucun produit : on reprend le bloc « Produits » des marques.
    const brandBlock =
      brandDoc?.getElementById('predictive-search-products') || pinnedDoc?.getElementById('predictive-search-products');
    if (!brandBlock) return markup;
    const block = /** @type {Element} */ (doc.importNode(brandBlock, true));
    list = block.querySelector('.predictive-search-results__wrapper-products');
    if (!list) return markup;
    inner.querySelector('.predictive-search-results__no-results')?.remove();
    const queries = inner.querySelector('.predictive-search-results__wrapper-queries');
    if (queries) {
      queries.after(block);
    } else {
      inner.append(block);
    }
  }

  list.replaceChildren(...ranked);

  if (ranked.length > 1) {
    inner.querySelector('[data-single-result-url]')?.remove();
  }

  return doc.body.innerHTML;
}

/**
 * @param {(sectionId: string, useCache?: boolean, url?: URL, signal?: AbortSignal) => Promise<string>} getSectionHTML
 * @param {string} sectionId
 * @param {boolean} useCache
 * @param {URL} url
 * @param {AbortSignal | undefined} signal
 */
async function boostedSearch(getSectionHTML, sectionId, useCache, url, signal) {
  const term = url.searchParams.get('q') || '';
  const words = searchWords(term);
  const nativePromise = getSectionHTML(sectionId, useCache, new URL(url.toString()), signal);
  if (words.length === 0) return nativePromise;

  const brandUrl = new URL(url.toString());
  brandUrl.searchParams.set('q', `${term} ${BRANDS_QUERY}`);
  brandUrl.searchParams.delete('resources[limit_scope]');
  brandUrl.searchParams.set('resources[type]', 'product');
  brandUrl.searchParams.set('resources[limit]', '10');

  const pinnedUrl = new URL(url.toString());
  pinnedUrl.searchParams.set('q', PINNED_QUERY);
  pinnedUrl.searchParams.delete('resources[limit_scope]');
  pinnedUrl.searchParams.set('resources[type]', 'product');
  pinnedUrl.searchParams.set('resources[limit]', String(PINNED.length));

  const brandPromise = getSectionHTML(sectionId, useCache, brandUrl, signal).catch(() => null);
  const pinnedPromise = getSectionHTML(sectionId, useCache, pinnedUrl, signal).catch(() => null);
  const [markup, brandMarkup, pinnedMarkup] = await Promise.all([nativePromise, brandPromise, pinnedPromise]);

  if (!markup) return markup;

  try {
    return mergeResults(markup, brandMarkup, pinnedMarkup, words);
  } catch (error) {
    return markup;
  }
}

if (!(/** @type {any} */ (sectionRenderer).__bxSearchBoost)) {
  /** @type {any} */ (sectionRenderer).__bxSearchBoost = true;

  const originalGetSectionHTML = sectionRenderer.getSectionHTML.bind(sectionRenderer);

  /**
   * @param {string} sectionId
   * @param {boolean} [useCache]
   * @param {URL} [url]
   * @param {AbortSignal} [signal]
   */
  sectionRenderer.getSectionHTML = function (sectionId, useCache = true, url = new URL(window.location.href), signal) {
    try {
      const predictivePath = new URL(Theme.routes.predictive_search_url, location.origin).pathname;
      if (
        url instanceof URL &&
        url.pathname === predictivePath &&
        url.searchParams.get('q') &&
        !url.searchParams.has('resources[type]')
      ) {
        return boostedSearch(originalGetSectionHTML, sectionId, useCache, url, signal);
      }
    } catch (error) {
      // on retombe sur la recherche normale
    }
    return originalGetSectionHTML(sectionId, useCache, url, signal);
  };
}
